import React from "react";
import {
  X,
  AlertCircle,
  Loader,
  FileText,
  Calendar,
  Code,
  Shield,
} from "lucide-react";
import { getWorkflowApprovalLabel } from "../Constant/EmployeeActions.config";

const RejectModal = ({
  t,
  styles,
  theme,
  isRTL,
  language,
  actionData,
  rejectionReason,
  setRejectionReason,
  isSubmitting,
  errorMsg,
  setErrorMsg,
  ACTION_TYPES,
  workflowDefinitions,
  onConfirm,
  onClose,
}) => {
  const actionConfig = ACTION_TYPES.find(
    (a) => a.id === actionData?.actionType
  );
  const ActionIcon = actionConfig?.icon || FileText;

  return (
    <div style={styles.modal} onClick={onClose} data-reject-modal>
      <div
        style={{ ...styles.modalContent, maxWidth: "min(90vw, 620px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.modalHeader}>
          <div
            style={{
              display: "flex",
              flexDirection: isRTL ? "row" : "row",
              alignItems: "center",
            }}
          >
            <div
              style={{
                ...styles.modalIconWrapper,
                background: "linear-gradient(135deg, #ef4444, #dc2626)",
              }}
            >
              <AlertCircle size={26} color="white" />
            </div>
            <div>
              <h2 style={styles.modalTitle}>{t.reject}</h2>
              <p
                style={{
                  fontSize: "clamp(12px, 1.7vw, 14px)",
                  color:
                    theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                  margin: "4px 0 0 0",
                  fontWeight: "600",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t.reviewBeforeConfirm}
              </p>
            </div>
          </div>
          <button
            style={styles.closeButton}
            onClick={onClose}
            className="enhanced-button-hover"
          >
            <X size={24} />
          </button>
        </div>

        {/* Loading overlay */}
        {isSubmitting && (
          <div style={styles.loadingOverlay}>
            <div style={{ textAlign: "center" }}>
              <Loader
                size={48}
                style={styles.loadingSpinner}
                color="#ef4444"
              />
              <p
                style={{
                  marginTop: "16px",
                  fontWeight: "600",
                  color:
                    theme === "dark" ? "rgba(255,255,255,0.8)" : "#64748b",
                }}
              >
                {t.processing}
              </p>
            </div>
          </div>
        )}

        {/* Action info card */}
        <div style={styles.infoBox}>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{t.employee}</span>
            <span style={styles.infoValue}>
              {language === "ar"
                ? actionData.employeeArabicName ||
                  actionData.employeeName ||
                  actionData.newData?.employeeName ||
                  t.notAvailable
                : actionData.employeeName ||
                  actionData.employeeArabicName ||
                  actionData.newData?.employeeName ||
                  t.notAvailable}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{t.infinityCode}</span>
            <span style={styles.infoValue}>
              <div
                style={{
                  display: "flex",
                  flexDirection: isRTL ? "row" : "row",
                  alignItems: "center",
                  gap: "6px",
                  justifyContent: isRTL ? "flex-start" : "flex-end",
                }}
              >
                <Code size={14} />
                {actionData.clientCode ||
                  actionData.employee?.clientCode ||
                  actionData.infinityCode ||
                  actionData.employeeId ||
                  t.notAvailable}
              </div>
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{t.actionType}</span>
            <div
              style={{
                ...styles.badge,
                background:
                  actionConfig?.bgColor || "rgba(100, 116, 139, 0.15)",
                border: `1px solid ${
                  actionConfig?.borderColor || "rgba(100, 116, 139, 0.4)"
                }`,
                color: theme === "dark" ? "#ffffff" : "#0f172a",
              }}
            >
              <ActionIcon size={14} />
              {actionConfig ? t[actionConfig.labelKey] : actionData.actionType}
            </div>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{t.effectiveDate}</span>
            <span style={styles.infoValue}>
              <div
                style={{
                  display: "flex",
                  flexDirection: isRTL ? "row" : "row",
                  alignItems: "center",
                  gap: "6px",
                  justifyContent: isRTL ? "flex-start" : "flex-end",
                }}
              >
                <Calendar size={14} />
                {actionData.effectiveDate
                  ? new Date(actionData.effectiveDate).toLocaleDateString(
                      language === "ar" ? "ar-EG" : "en-US"
                    )
                  : t.notAvailable}
              </div>
            </span>
          </div>
          {actionConfig?.requiresApproval && (
            <div
              style={{
                ...styles.infoRow,
                borderBottom: "none",
                marginBottom: 0,
                paddingBottom: 0,
              }}
            >
              <span style={styles.infoLabel}>{t.approvalLevel}</span>
              <div
                style={{
                  ...styles.badge,
                  background: "rgba(139, 92, 246, 0.15)",
                  border: "1px solid rgba(139, 92, 246, 0.4)",
                  color: theme === "dark" ? "#c4b5fd" : "#7c3aed",
                }}
              >
                <Shield size={14} />
                {getWorkflowApprovalLabel(
                  actionConfig.id,
                  workflowDefinitions,
                  language,
                  t,
                  actionConfig.approvalLevelKey,
                  actionData?.employee?.department?.name ??
                    actionData?.oldDepartment ??
                    actionData?.newDepartment ??
                    null
                ) || t.notAvailable}
              </div>
            </div>
          )}
        </div>

        {/* Original reason (if any) */}
        {actionData.reason && (
          <div
            style={{
              ...styles.infoBox,
              background:
                theme === "dark"
                  ? "rgba(245, 158, 11, 0.08)"
                  : "rgba(245, 158, 11, 0.06)",
              border:
                theme === "dark"
                  ? "1px solid rgba(245, 158, 11, 0.2)"
                  : "1px solid rgba(245, 158, 11, 0.15)",
            }}
          >
            <div
              style={{
                fontSize: "clamp(11px, 1.6vw, 13px)",
                color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                marginBottom: "8px",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {t.reason}
            </div>
            <div
              style={{
                fontSize: "clamp(13px, 1.9vw, 15px)",
                fontWeight: "600",
                color: theme === "dark" ? "#ffffff" : "#0f172a",
                lineHeight: "1.6",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {actionData.reason}
            </div>
          </div>
        )}

        {/* Error message */}
        {errorMsg && (
          <div
            style={{ ...styles.alertBox, ...styles.errorAlert }}
            className="fade-in-alert"
          >
            <AlertCircle size={20} />
            <span style={{ fontWeight: "700" }}>{errorMsg}</span>
          </div>
        )}

        {/* Rejection reason textarea */}
        <div style={{ ...styles.controlGroup, marginTop: "8px" }}>
          <label style={styles.label}>
            <AlertCircle size={15} />
            {t.reasonForAction} *
          </label>
          <textarea
            style={styles.textarea}
            className="enhanced-input-focus"
            value={rejectionReason}
            onChange={(e) => {
              setRejectionReason(e.target.value);
              if (errorMsg) setErrorMsg("");
            }}
            placeholder={t.provideDetailedReason}
            rows={4}
            dir={isRTL ? "rtl" : "ltr"}
          />
        </div>

        {/* Action buttons */}
        <div
          style={{
            display: "flex",
            flexDirection: isRTL ? "row" : "row",
            gap: "14px",
            marginTop: "clamp(24px, 3.8vw, 32px)",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            style={{ ...styles.secondaryButton, flex: 1 }}
            onClick={onClose}
            disabled={isSubmitting}
          >
            <X size={18} /> {t.cancel}
          </button>
          <button
            type="button"
            style={{
              ...styles.primaryButton,
              flex: 1,
              background: "linear-gradient(135deg, #ef4444, #dc2626)",
            }}
            onClick={onConfirm}
            className="enhanced-button-hover"
            disabled={isSubmitting || !rejectionReason.trim()}
          >
            {isSubmitting ? (
              <>
                <Loader size={18} style={styles.loadingSpinner} />{" "}
                {t.processing}
              </>
            ) : (
              <>
                <AlertCircle size={18} /> {t.reject}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectModal;
